import React, { useState, useEffect, useCallback } from 'react';
import {
  FaRupeeSign,
  FaCheckCircle,
  FaTimesCircle,
  FaCalendarAlt,
  FaSearch,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaUser,
  FaMoneyBillWave,
  FaDownload,
  FaChartLine,
  FaChartPie,
  FaChartBar
} from 'react-icons/fa';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import CustomSelect from '../../components/CustomSelect/CustomSelect';
import { apiHelper } from '../../utils/api';
import './Admin.scss';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatDateTime = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const CHART_COLORS = { primary: '#6366F1', success: '#10B981', warning: '#F59E0B', danger: '#EF4444', info: '#3B82F6', gold: '#F59E0B', silver: '#9CA3AF' };
const PIE_COLORS = ['#6366F1', '#10B981', '#F59E0B', '#EF4444', '#3B82F6'];

const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 15, total: 0, pages: 0 });
  const [charts, setCharts] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(search);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPagination(prev => (prev.page !== 1 ? { ...prev, page: 1 } : prev));
  }, [debouncedSearch, statusFilter, period, customDateFrom, customDateTo]);

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        period,
        page: String(pagination.page),
        limit: String(pagination.limit),
        ...(period === 'custom' && customDateFrom && customDateTo ? { startDate: customDateFrom, endDate: customDateTo } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      });
      const data = await apiHelper.get(`/admin/payments?${params.toString()}`);
      setPayments(data.payments);
      setStats(data.stats);
      setCharts(data.charts);
      setPagination(data.pagination);
    } catch (err) {
      console.error('Error fetching payments:', err);
    } finally {
      setLoading(false);
    }
  }, [period, customDateFrom, customDateTo, statusFilter, debouncedSearch, pagination.page, pagination.limit]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handlePeriodChange = (p) => {
    setPeriod(p);
    setPagination(prev => ({ ...prev, page: 1 }));
    if (p !== 'custom') {
      setCustomDateFrom('');
      setCustomDateTo('');
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const exportPayments = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({
        period,
        export: 'true',
        ...(period === 'custom' && customDateFrom && customDateTo ? { startDate: customDateFrom, endDate: customDateTo } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(search.trim() ? { search: search.trim() } : {}),
      });
      const data = await apiHelper.get(`/admin/payments?${params.toString()}`);
      const csvEscape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const rows = data.payments.map(p => [
        p.orderId || '',
        p.paymentId || '',
        p.userId?.name || 'Unknown',
        p.userId?.email || '',
        p.plan || '',
        p.amount != null ? String(p.amount) : '',
        p.status || '',
        // Use ISO-like sortable format and let csvEscape handle commas/quotes
        new Date(p.createdAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }),
      ]);
      const headers = ['Order ID', 'Payment ID', 'User', 'Email', 'Plan', 'Amount', 'Status', 'Date & Time'];
      const csv = [headers.map(csvEscape).join(','), ...rows.map(r => r.map(csvEscape).join(','))].join('\r\n');
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `payments_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="admin-payments">
      <div className="admin-page-header">
        <div className="admin-page-header__left">
          <h2><FaMoneyBillWave /> Payments</h2>
          <p>View and manage all payment transactions</p>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="admin-analytics-metrics">
          <div className="admin-metric-card">
            <div className="admin-metric-card__icon admin-metric-card__icon--success">
              <FaRupeeSign />
            </div>
            <div className="admin-metric-card__content">
              <span className="admin-metric-card__value">{formatCurrency(stats.periodRevenue)}</span>
              <span className="admin-metric-card__label">Period Revenue</span>
              <span className="admin-metric-card__sub">Selected period</span>
            </div>
          </div>
          <div className="admin-metric-card">
            <div className="admin-metric-card__icon admin-metric-card__icon--primary">
              <FaRupeeSign />
            </div>
            <div className="admin-metric-card__content">
              <span className="admin-metric-card__value">{formatCurrency(stats.allTimeRevenue)}</span>
              <span className="admin-metric-card__label">All-Time Revenue</span>
              <span className="admin-metric-card__sub">Total successful</span>
            </div>
          </div>
          <div className="admin-metric-card">
            <div className="admin-metric-card__icon admin-metric-card__icon--success">
              <FaCheckCircle />
            </div>
            <div className="admin-metric-card__content">
              <span className="admin-metric-card__value">{stats.successfulCount}</span>
              <span className="admin-metric-card__label">Successful</span>
              <span className="admin-metric-card__sub">In period</span>
            </div>
          </div>
          <div className="admin-metric-card">
            <div className="admin-metric-card__icon admin-metric-card__icon--danger">
              <FaTimesCircle />
            </div>
            <div className="admin-metric-card__content">
              <span className="admin-metric-card__value">{stats.failedCount}</span>
              <span className="admin-metric-card__label">Failed</span>
              <span className="admin-metric-card__sub">In period</span>
            </div>
          </div>
        </div>
      )}

      {/* Charts */}
      {charts && (
        <div className="admin-charts-row" style={{ marginBottom: 24 }}>
          <div className="admin-chart-card admin-chart-card--large">
            <div className="admin-chart-card__header">
              <h3 style={{ fontSize: 15, fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: 8 }}>
                <FaChartLine style={{ color: '#6366f1' }} />
                Daily Revenue Trend
              </h3>
            </div>
            <div className="admin-chart-card__body">
              {charts.dailyRevenue.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={charts.dailyRevenue}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <defs>
                      <linearGradient id="payRevGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <Area type="monotone" dataKey="revenue" stroke="#6366f1" fill="url(#payRevGrad)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>No revenue data for this period.</div>
              )}
            </div>
          </div>
          <div className="admin-chart-card">
            <div className="admin-chart-card__header">
              <h3 style={{ fontSize: 15, fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: 8 }}>
                <FaChartBar style={{ color: '#10B981' }} />
                Revenue by Plan
              </h3>
            </div>
            <div className="admin-chart-card__body">
              {charts.revenueByPlan.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={charts.revenueByPlan.map(d => ({ ...d, name: d._id }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="total" name="Revenue" fill="#6366f1" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>No plan revenue data.</div>
              )}
            </div>
          </div>
          <div className="admin-chart-card">
            <div className="admin-chart-card__header">
              <h3 style={{ fontSize: 15, fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: 8 }}>
                <FaChartPie style={{ color: '#F59E0B' }} />
                Payment Distribution
              </h3>
            </div>
            <div className="admin-chart-card__body">
              {charts.paymentMethodDist.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie data={charts.paymentMethodDist} dataKey="count" nameKey="_id" cx="50%" cy="50%" outerRadius={80} label={({ _id, count }) => `${_id}: ${count}`}>
                      {charts.paymentMethodDist.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>No payment data.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="admin-payments-filters">
        <div className="admin-payments-filter-inner">
          <FaCalendarAlt className="admin-payments-filter-icon" />
          <CustomSelect
            value={period}
            onChange={(val) => handlePeriodChange(val)}
            className="admin-select"
            options={[
              { value: 'all', label: 'All Time' },
              { value: '7', label: 'Last 7 days' },
              { value: '30', label: 'Last 30 days' },
              { value: '90', label: 'Last 90 days' },
              { value: '365', label: 'Last year' },
              { value: 'custom', label: 'Custom Range' },
            ]}
          />
          {period === 'custom' && (
            <div className="admin-date-range">
              <input
                type="date"
                value={customDateFrom}
                onChange={(e) => { setCustomDateFrom(e.target.value); setPagination(prev => ({ ...prev, page: 1 })); }}
                max={customDateTo || new Date().toISOString().split('T')[0]}
                className="admin-date-input"
              />
              <span className="admin-date-separator">to</span>
              <input
                type="date"
                value={customDateTo}
                onChange={(e) => { setCustomDateTo(e.target.value); setPagination(prev => ({ ...prev, page: 1 })); }}
                min={customDateFrom}
                max={new Date().toISOString().split('T')[0]}
                className="admin-date-input"
              />
            </div>
          )}
          <CustomSelect
            value={statusFilter}
            onChange={(val) => { setStatusFilter(val); setPagination(prev => ({ ...prev, page: 1 })); }}
            className="admin-select"
            options={[
              { value: '', label: 'All Status' },
              { value: 'success', label: 'Successful' },
              { value: 'failed', label: 'Failed' },
            ]}
          />
          <div className="admin-payments-search">
            <FaSearch />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button className="admin-search__clear" onClick={() => setSearch('')}>
                <FaTimes />
              </button>
            )}
          </div>
          <button className="btn btn-primary" onClick={exportPayments} disabled={exporting} style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
            <FaDownload /> {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="admin-table-container">
        {loading ? (
          <div className="admin-loading">
            <div className="admin-loading__spinner" />
            <p>Loading payments...</p>
          </div>
        ) : payments.length === 0 ? (
          <div className="admin-empty-state">
            <FaMoneyBillWave />
            <h3>No payments found</h3>
            <p>Try adjusting your filters</p>
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Payment ID</th>
                <th>User</th>
                <th>Plan</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Date & Time</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment) => (
                <tr key={payment._id}>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#6b7280' }}>
                    {payment.orderId || '-'}
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#6b7280' }}>
                    {payment.paymentId ? payment.paymentId.slice(-12) : '-'}
                  </td>
                  <td>
                    <div className="admin-user-cell">
                      <div className="admin-user-cell__avatar">
                        {payment.userId?.name?.charAt(0) || <FaUser />}
                      </div>
                      <div className="admin-user-cell__info">
                        <span className="admin-user-cell__name">
                          {payment.userId?.name || 'Unknown User'}
                        </span>
                        <span className="admin-user-cell__email">
                          {payment.userId?.email || '-'}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className={`admin-plan-badge admin-plan-badge--${payment.plan}`}>
                      <span>{payment.plan}</span>
                    </div>
                  </td>
                  <td style={{ fontWeight: 600 }}>
                    {formatCurrency(payment.amount)}
                  </td>
                  <td>
                    <span className={`admin-status-badge admin-status-badge--${payment.status === 'success' ? 'active' : 'inactive'}`}>
                      {payment.status === 'success' ? <><FaCheckCircle /> Success</> : <><FaTimesCircle /> Failed</>}
                    </span>
                  </td>
                  <td style={{ color: '#6b7280', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                    {formatDateTime(payment.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination.total > 0 && (
        <div className="admin-pagination">
          <div className="admin-pagination__info">
            <span>
              Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
              {pagination.total} results
            </span>
          </div>
          <div className="admin-pagination__controls">
            <button
              className="admin-pagination__btn"
              disabled={pagination.page === 1 || loading}
              onClick={() => handlePageChange(1)}
            >
              First
            </button>
            <button
              className="admin-pagination__btn"
              disabled={pagination.page === 1 || loading}
              onClick={() => handlePageChange(pagination.page - 1)}
            >
              <FaChevronLeft /> Previous
            </button>
            <span className="admin-pagination__page-info">
              Page {pagination.page} of {pagination.pages}
            </span>
            <button
              className="admin-pagination__btn"
              disabled={pagination.page === pagination.pages || loading}
              onClick={() => handlePageChange(pagination.page + 1)}
            >
              Next <FaChevronRight />
            </button>
            <button
              className="admin-pagination__btn"
              disabled={pagination.page === pagination.pages || loading}
              onClick={() => handlePageChange(pagination.pages)}
            >
              Last
            </button>
          </div>
          <div className="admin-pagination__size">
            <label>Per page:</label>
            <CustomSelect
              value={pagination.limit}
              onChange={(newLimit) => setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }))}
              options={[
                { value: 15, label: '15' },
                { value: 25, label: '25' },
                { value: 50, label: '50' },
              ]}
              disabled={loading}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Payments;
